
import { defineComponent, ref, onMounted, watch, computed } from "vue"
import { Actions } from "@/store/enums/StoreEnums"
import { useStore } from "vuex"
import { useRoute, useRouter } from "vue-router"
import { setCurrentPageBreadcrumbs } from "@/core/helpers/breadcrumb"
import { ElMessage } from "element-plus"
import type { ElForm } from "element-plus"
import { MallCoupon } from "@/store/modules/MallCouponModule"

type FormInstance = InstanceType<typeof ElForm>;

interface CouponForm {
  name: string;
  coupon_type: string;
  discount_value: number;
  min_amount: number;
  max_discount: number | null;
  total_quantity: number | null;
  per_user_limit: number | null;
  start_time: string | null;
  end_time: string | null;
  validity_days: number | null;
  status: string;
  allow_user_claim: boolean;
  product_scope: string;
  product_ids: number[];
  return_days: number;
}

export default defineComponent({
  setup() {
    const store = useStore()
    const route = useRoute()
    const router = useRouter()
    const formRef = ref<FormInstance>()

    const isNew = !route.params.id

    onMounted(() => {
      if (isNew) {
        setCurrentPageBreadcrumbs("新建优惠券", ["商城优惠券管理;mall_coupons"])
      } else {
        setCurrentPageBreadcrumbs("编辑优惠券", ["商城优惠券管理;mall_coupons"])
      }
    })

    const coupon = ref<CouponForm>({
      name: '',
      coupon_type: 'fixed',
      discount_value: 10,
      min_amount: 0,
      max_discount: null,
      total_quantity: null,
      per_user_limit: null,
      start_time: null,
      end_time: null,
      validity_days: null,
      status: 'active',
      allow_user_claim: true,
      product_scope: 'all_products',
      product_ids: [],
      return_days: 7
    })

    const validityType = ref<string>('forever')
    const timeRange = ref<[string, string] | null>(null)

    // 监听 validityType 变化，清空相关字段
    watch(validityType, (val) => {
      if (val === 'forever') {
        coupon.value.start_time = null
        coupon.value.end_time = null
        coupon.value.validity_days = null
        timeRange.value = null
      } else if (val === 'range') {
        coupon.value.validity_days = null
      } else if (val === 'days') {
        coupon.value.start_time = null
        coupon.value.end_time = null
        timeRange.value = null
      }
    })

    // 监听 timeRange 变化，同步到 coupon
    watch(timeRange, (val) => {
      if (val && val.length === 2) {
        coupon.value.start_time = val[0]
        coupon.value.end_time = val[1]
      } else {
        coupon.value.start_time = null
        coupon.value.end_time = null
      }
    })

    const productSearchLoading = ref(false)
    const productOptions = ref<Array<{ id: number; name: string }>>([])

    const rules = ref({
      name: [
        { required: true, message: '请输入优惠券名称', trigger: 'blur' },
        { min: 1, max: 50, message: '名称长度需在 1-50 之间', trigger: 'blur' }
      ],
      coupon_type: [
        { required: true, message: '请选择优惠券类型', trigger: 'change' }
      ],
      discount_value: [
        {
          validator: (rule: any, value: any, callback: any) => {
            if (value === undefined || value === null || value <= 0) {
              callback(new Error('请输入有效的优惠值'))
              return
            }
            if (coupon.value.coupon_type === 'percent' && (value < 1 || value > 99)) {
              callback(new Error('折扣比例需在 1-99 之间'))
              return
            }
            callback()
          },
          trigger: ['blur', 'change']
        }
      ],
      product_scope: [
        { required: true, message: '请选择适用范围', trigger: 'change' }
      ],
      product_ids: [
        {
          validator: (rule: any, value: any, callback: any) => {
            if (coupon.value.product_scope === 'specific_products' && (!value || value.length === 0)) {
              callback(new Error('请选择适用商品'))
              return
            }
            callback()
          },
          trigger: 'change'
        }
      ]
    })

    // 加载优惠券详情（编辑模式）
    if (!isNew) {
      store
        .dispatch(Actions.GET_MALL_COUPON, route.params.id)
        .then(() => {
          const data = store.getters.currentMallCoupon
          coupon.value = {
            name: data.name,
            coupon_type: data.coupon_type,
            discount_value: data.discount_value,
            min_amount: data.min_amount,
            max_discount: data.max_discount,
            total_quantity: data.total_quantity,
            per_user_limit: data.per_user_limit,
            start_time: data.start_time,
            end_time: data.end_time,
            validity_days: data.validity_days,
            status: data.status,
            allow_user_claim: data.allow_user_claim ?? true,
            product_scope: data.product_scope,
            product_ids: data.product_ids || [],
            return_days: data.return_days || 7
          }

          // 判断有效期类型
          if (data.start_time && data.end_time) {
            validityType.value = 'range'
            timeRange.value = [data.start_time, data.end_time]
          } else if (data.validity_days) {
            validityType.value = 'days'
          } else {
            validityType.value = 'forever'
          }

          // 如果有指定商品，加载商品选项
          if (data.product_ids && data.product_ids.length > 0) {
            loadProductsByIds(data.product_ids)
          }
        })
        .catch(() => {
          ElMessage.error('获取优惠券详情失败')
        })
    }

    const searchProducts = (query: string) => {
      if (query.length < 2) {
        return
      }
      productSearchLoading.value = true
      store
        .dispatch(Actions.GET_PRODUCTS, { name: query, is_mall_product: true, pageSize: 20 })
        .then(() => {
          productOptions.value = store.getters.currentProducts.map((p: any) => ({
            id: p.id,
            name: p.name
          }))
          productSearchLoading.value = false
        })
        .catch(() => {
          productSearchLoading.value = false
        })
    }

    const loadProductsByIds = (ids: number[]) => {
      // 批量加载已选商品的名称
      ids.forEach(id => {
        store
          .dispatch(Actions.GET_PRODUCT, id)
          .then(() => {
            const product = store.getters.currentProduct
            if (product && !productOptions.value.find(p => p.id === product.id)) {
              productOptions.value.push({
                id: product.id,
                name: product.name
              })
            }
          })
          .catch(() => {
            // ignore
          })
      })
    }

    const onSubmit = (formEl: FormInstance | undefined) => {
      if (!formEl) return

      formEl.validate((valid) => {
        if (valid) {
          // 后端 coupon_params 使用 params.permit(...) 直接获取参数，不需要嵌套
          const params = {
            ...coupon.value,
            total_quantity: coupon.value.total_quantity || null,
            per_user_limit: coupon.value.per_user_limit || null,
            max_discount: coupon.value.max_discount || null,
            validity_days: validityType.value === 'days' ? coupon.value.validity_days : null
          }

          if (isNew) {
            store
              .dispatch(Actions.CREATE_MALL_COUPON, params)
              .then(() => {
                ElMessage.success('创建成功')
                router.push({ name: 'mall_coupons' })
              })
              .catch(() => {
                ElMessage.error('创建失败')
              })
          } else {
            store
              .dispatch(Actions.UPDATE_MALL_COUPON, {
                id: route.params.id,
                values: params
              })
              .then(() => {
                ElMessage.success('更新成功')
                router.push({ name: 'mall_coupons' })
              })
              .catch(() => {
                ElMessage.error('更新失败')
              })
          }
        }
      })
    }

    const onCancel = () => {
      router.push({ name: 'mall_coupons' })
    }

    return {
      formRef,
      coupon,
      rules,
      isNew,
      validityType,
      timeRange,
      productSearchLoading,
      productOptions,
      searchProducts,
      onSubmit,
      onCancel
    }
  }
})
